import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  Users, 
  Plus, 
  Search, 
  X, 
  User,
  Star,
  Trash2,
  Edit,
  Save,
  MessageSquare
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamLeaderTeams() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [workerCode, setWorkerCode] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchingWorker, setSearchingWorker] = useState(false);
  
  // Team Chat state
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [activeChatTeam, setActiveChatTeam] = useState(null);
  const [chatMessage, setChatMessage] = useState("");
  const [teamMessages, setTeamMessages] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  // Team Chat functions
  const openTeamChat = async (team) => {
    setActiveChatTeam(team);
    setChatDialogOpen(true);
    await fetchTeamMessages(team.team_id);
  };

  const fetchTeamMessages = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/chat`);
      setTeamMessages(response.data);
    } catch (error) {
      // If no messages or endpoint doesn't exist yet, set empty
      setTeamMessages([]);
    }
  };

  const sendTeamMessage = async () => {
    if (!chatMessage.trim() || !activeChatTeam) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/teams/${activeChatTeam.team_id}/chat`, {
        content: chatMessage.trim()
      });
      setChatMessage("");
      await fetchTeamMessages(activeChatTeam.team_id);
      toast.success("Message sent to team");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSearchWorker = async () => {
    if (!workerCode.trim()) return;
    
    setSearchingWorker(true);
    try {
      const response = await axios.get(`${API}/workers/search`, {
        params: { code: workerCode.trim().toUpperCase() }
      });
      
      if (response.data.length > 0) {
        const worker = response.data[0];
        if (!selectedMembers.find(m => m.user_id === worker.user_id)) {
          setSelectedMembers([...selectedMembers, worker]);
          setWorkerCode("");
          toast.success(`${worker.name} added`);
        } else {
          toast.info("Worker already in team");
        }
      } else {
        toast.error("Worker not found");
      }
    } catch (error) {
      toast.error("Failed to search worker");
    } finally {
      setSearchingWorker(false);
    }
  };

  const handleRemoveMember = (userId) => {
    setSelectedMembers(selectedMembers.filter(m => m.user_id !== userId));
  };

  const resetForm = () => {
    setTeamName("");
    setTeamDescription("");
    setWorkerCode("");
    setSelectedMembers([]);
    setEditingTeam(null);
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/teams`, {
        name: teamName.trim(),
        description: teamDescription.trim(),
        leader_id: user.user_id,
        member_ids: selectedMembers.map(m => m.user_id),
      });
      toast.success("Team created successfully!");
      setCreateDialogOpen(false);
      resetForm();
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/teams/${editingTeam.team_id}`, {
        name: teamName.trim(),
        description: teamDescription.trim(),
        leader_id: user.user_id,
        member_ids: selectedMembers.map(m => m.user_id),
      });
      toast.success("Team updated successfully!");
      setEditingTeam(null);
      resetForm();
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm("Are you sure you want to delete this team?")) return;

    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success("Team deleted");
      fetchTeams();
    } catch (error) {
      toast.error("Failed to delete team");
    }
  };

  const openEditDialog = (team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setTeamDescription(team.description || "");
    setSelectedMembers(team.members || []);
    setCreateDialogOpen(true);
  };

  return (
    <DashboardLayout navItems={teamLeaderNavItems} title="My Teams">
      <div className="space-y-6" data-testid="team-leader-teams">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-['Unbounded'] font-semibold text-white">
              Team Management
            </h2>
            <p className="text-[#94A3B8]">Create and manage your teams for project applications</p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            setCreateDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold" data-testid="create-team-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Unbounded']">
                  {editingTeam ? "Edit Team" : "Create New Team"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Team Name */}
                <div className="space-y-2">
                  <Label className="text-white">Team Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="e.g., Alpha Team"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Description (optional)</Label>
                  <Textarea
                    value={teamDescription}
                    onChange={(e) => setTeamDescription(e.target.value)}
                    placeholder="Brief description of this team..."
                    rows={2}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>

                {/* Add Members */}
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    <Search className="w-4 h-4 text-[#23F7DD]" />
                    Add Team Members by Worker Code
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={workerCode}
                      onChange={(e) => setWorkerCode(e.target.value.toUpperCase())}
                      placeholder="e.g., WRK-001"
                      className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                      onKeyDown={(e) => e.key === "Enter" && handleSearchWorker()}
                    />
                    <Button 
                      onClick={handleSearchWorker} 
                      disabled={searchingWorker}
                      className="bg-[#23F7DD]/20 text-[#23F7DD] hover:bg-[#23F7DD]/30 border border-[#23F7DD]/30"
                    >
                      {searchingWorker ? "..." : "Add"}
                    </Button>
                  </div>
                </div>

                {/* Selected Members */}
                {selectedMembers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-white">Team Members ({selectedMembers.length})</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedMembers.map((member) => (
                        <div 
                          key={member.user_id}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${member.is_available ? 'bg-green-400' : 'bg-red-400'}`} />
                            <div>
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-xs text-[#94A3B8]">
                                {member.worker_code} 
                                {member.average_rating > 0 && (
                                  <span className="ml-2 inline-flex items-center">
                                    <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                    {member.average_rating.toFixed(1)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreateDialogOpen(false);
                      resetForm();
                    }}
                    className="border-white/20 text-[#94A3B8]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={editingTeam ? handleUpdateTeam : handleCreateTeam}
                    disabled={saving}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  >
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingTeam ? "Update Team" : "Create Team"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Teams List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : teams.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 text-[#475569] mx-auto mb-4" />
              <h3 className="text-xl font-['Unbounded'] text-white mb-2">No Teams Yet</h3>
              <p className="text-[#94A3B8] mb-6">Create your first team to start applying for projects together</p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {teams.map((team) => (
              <Card key={team.team_id} className="bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/30 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white font-['Unbounded'] text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#23F7DD]" />
                      {team.name}
                    </CardTitle>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTeamChat(team)}
                        className="text-[#94A3B8] hover:text-[#23F7DD] hover:bg-[#23F7DD]/10"
                        title="Team Chat"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(team)}
                        className="text-[#94A3B8] hover:text-[#23F7DD] hover:bg-[#23F7DD]/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTeam(team.team_id)}
                        className="text-[#94A3B8] hover:text-red-400 hover:bg-red-400/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {team.description && (
                    <p className="text-sm text-[#94A3B8]">{team.description}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-xs text-[#94A3B8] uppercase tracking-wider">
                      Members ({team.members?.length || 0})
                    </p>
                    {team.members && team.members.length > 0 ? (
                      <div className="space-y-2">
                        {team.members.slice(0, 4).map((member) => (
                          <div 
                            key={member.user_id}
                            className="flex items-center justify-between p-2 bg-white/5 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${member.is_available ? 'bg-green-400' : 'bg-gray-500'}`} />
                              <div className="w-6 h-6 rounded-full bg-[#23F7DD]/10 flex items-center justify-center">
                                <User className="w-3 h-3 text-[#23F7DD]" />
                              </div>
                              <span className="text-white text-sm">{member.name}</span>
                            </div>
                            <span className="text-[#475569] text-xs">{member.worker_code}</span>
                          </div>
                        ))}
                        {team.members.length > 4 && (
                          <p className="text-xs text-[#94A3B8] text-center">
                            +{team.members.length - 4} more members
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#94A3B8]">No members added yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Team Chat Dialog */}
        <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#23F7DD]" />
                {activeChatTeam?.name} - Team Chat
              </DialogTitle>
            </DialogHeader>
            
            {/* Members list */}
            <div className="flex flex-wrap gap-1 py-2 border-b border-white/10">
              {activeChatTeam?.members?.map((member) => (
                <span 
                  key={member.user_id}
                  className="px-2 py-1 bg-white/5 rounded text-xs text-[#94A3B8]"
                >
                  {member.name}
                </span>
              ))}
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-[200px] max-h-[300px]">
              {teamMessages.length === 0 ? (
                <div className="text-center text-[#94A3B8] py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation with your team!</p>
                </div>
              ) : (
                teamMessages.map((msg) => (
                  <div 
                    key={msg.message_id}
                    className={`p-3 rounded-lg ${msg.sender_id === user?.user_id ? 'bg-[#23F7DD]/20 ml-8' : 'bg-white/5 mr-8'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[#23F7DD]">
                        {msg.sender_name}
                      </span>
                      <span className="text-xs text-[#475569]">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-white text-sm">{msg.content}</p>
                  </div>
                ))
              )}
            </div>
            
            {/* Message input */}
            <div className="flex gap-2 pt-3 border-t border-white/10">
              <Input
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Type a message to your team..."
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendTeamMessage()}
              />
              <Button
                onClick={sendTeamMessage}
                disabled={sendingMessage || !chatMessage.trim()}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                {sendingMessage ? "..." : "Send"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
